import { z } from "zod"

// ==================== PROVIDER ENUMS ====================

/**
 * Video generation providers
 * Used for tracking which provider is handling a video generation job
 */
export enum EVideoProvider {
  REPLICATE = "replicate",
  FAL = "fal",
  WAVESPEED = "wavespeed",
  KIE_AI = "kie-ai",
  // Future providers:
  // RUNWAY = "runway",
  // LUMA = "luma",
  // PIKA = "pika",
}

/**
 * Image generation providers
 * Used for tracking which provider is handling an image generation job
 */
export enum EImageProvider {
  REPLICATE = "replicate",
  FAL = "fal",
  OPENAI = "openai",
  WAVESPEED = "wavespeed",
  KIE_AI = "kie-ai",
  OPENROUTER = "openrouter",
  // Future providers:
  // MIDJOURNEY = "midjourney",
  // STABILITY = "stability",
}

/**
 * Model family for grouping related video models
 * Used for fallback instructions, pricing tiers, etc.
 */
export enum EVideoModelFamily {
  FLUX = "flux", // same string as EImageModelFamily.FLUX — one MODEL_FAMILIES row serves both modalities
  KLING = "kling",
  MINIMAX = "minimax",
  HEYGEN_VIDEO = "heygen-video",
  VEO = "veo",
  SEEDANCE = "seedance",
  WAN = "wan",
  SORA = "sora",
  INFINITETALK = "infinitetalk",
  PIXVERSE = "pixverse",
  RUNWAY = "runway",
  HUNYUAN = "hunyuan",
  LUMA = "luma",
  LTX = "ltx",
  VIDU = "vidu",
  CREATIFY = "creatify",
  SKYREELS = "skyreels",
  GROK = "grok",
  HAPPYHORSE = "happyhorse",
  GEMINI_OMNI = "gemini-omni",
}

/**
 * Audio generation providers
 */
export enum EAudioGenerationProvider {
  KIE_AI = "kie-ai",
  REPLICATE = "replicate",
}

/**
 * Audio model family for grouping related audio models
 */
export enum EAudioModelFamily {
  SUNO = "suno",
  MINIMAX = "minimax",
  RESEMBLE_AI = "resemble-ai",
  QWEN = "qwen",
  INWORLD = "inworld",
  GOOGLE = "google",
}

/**
 * Image model family for grouping related image models (VM-987).
 *
 * Sibling of `EVideoModelFamily` / `EAudioModelFamily` — image was the one modality with no
 * family concept, so the model picker had to infer it from id substrings on the client.
 * Per-model assignment lives in `IMAGE_MODEL_FAMILY` (`image/modelFamilies.ts`).
 *
 * Values are the lab/brand, not the run-host: Wan images route through several providers
 * but the family is still `wan`.
 */
export enum EImageModelFamily {
  FLUX = "flux",
  IMAGEN = "imagen",
  NANO_BANANA = "nano-banana",
  GEMINI = "gemini",
  GPT_IMAGE = "gpt-image",
  DALLE = "dalle",
  SEEDREAM = "seedream",
  IDEOGRAM = "ideogram",
  WAN = "wan",
  HIDREAM = "hidream",
  NUCLEUS = "nucleus",
  GROK = "grok",
  REVE = "reve",
  MAI = "mai-image",
  MOSS = "moss-image",
  KREA = "krea",
  RECRAFT = "recraft",
  QWEN = "qwen",
  SANA = "sana",
  Z_IMAGE = "z-image",
  MINIMAX = "minimax",
}

/**
 * Suno audio generation models (via Kie.ai)
 */
export enum SunoAudioGenerationModelType {
  SUNO_V4 = "suno/v4",
  SUNO_V4_5 = "suno/v4.5",
  SUNO_V4_5_PLUS = "suno/v4.5-plus",
  SUNO_V4_5_ALL = "suno/v4.5-all",
  SUNO_V5 = "suno/v5",
}

/**
 * MiniMax audio models (via Replicate)
 */
export enum MiniMaxAudioModelType {
  SPEECH_2_8_TURBO = "minimax/speech-2.8-turbo",
  VOICE_CLONING = "minimax/voice-cloning",
}

/**
 * Resemble AI audio models (via Replicate)
 */
export enum ResembleAIAudioModelType {
  CHATTERBOX_TURBO = "resemble-ai/chatterbox-turbo",
}

/**
 * Qwen audio models (via Replicate)
 */
export enum QwenAudioModelType {
  QWEN3_TTS = "qwen/qwen3-tts",
}

/**
 * Inworld audio models (via Replicate)
 *
 * Note: Inworld renamed `tts-1.5-max` → `realtime-tts-1.5-max` on Replicate (Apr 2026).
 * The old slug returns 200 on GET (redirect for browse) but 404 on POST /predictions.
 */
export enum InworldAudioModelType {
  TTS_1_5_MAX = "inworld/realtime-tts-1.5-max",
}

/**
 * Google audio models (via Replicate)
 */
export enum GoogleAudioModelType {
  GEMINI_3_1_FLASH_TTS = "google/gemini-3.1-flash-tts",
}

// Union type for all audio creation models (extensible — add new model enums to this union)
// NOTE: Named "AudioCreationModelType" to avoid conflict with AudioGenerationModelType in llms/enums.ts (TTS providers)
export type AudioCreationModelType =
  | SunoAudioGenerationModelType
  | MiniMaxAudioModelType
  | ResembleAIAudioModelType
  | QwenAudioModelType
  | InworldAudioModelType
  | GoogleAudioModelType

// Legacy alias for backward compat
export type MusicGenerationModelType = SunoAudioGenerationModelType

// Zod schemas for provider validation
export const zVideoProvider = z.nativeEnum(EVideoProvider)
export const zImageProvider = z.nativeEnum(EImageProvider)
export const zVideoModelFamily = z.nativeEnum(EVideoModelFamily)
export const zAudioGenerationProvider = z.nativeEnum(EAudioGenerationProvider)
export const zAudioModelFamily = z.nativeEnum(EAudioModelFamily)

// ==================== MODEL TYPE ENUMS ====================

export enum OpenAIImageGenerationModelType {
  DALLE_3 = "dall-e-3",
  GPT_IMAGE = "gpt-image-1",
  GPT_IMAGE_1_5 = "gpt-image-1.5", // Replicate: openai/gpt-image-1.5, OpenAI: gpt-image-1.5
  GPT_IMAGE_1_MINI = "gpt-image-1-mini", // Replicate: openai/gpt-image-1-mini, OpenAI: gpt-image-1-mini
  GPT_IMAGE_2 = "gpt-image-2", // Kie.ai: gpt-image-2-text-to-image — $0.08/img (4K), $0.05 (2K), $0.03 (1K). Also FAL + Replicate fallbacks.
  GPT_IMAGE_2_EDIT = "gpt-image-2-edit", // Kie.ai: gpt-image-2-image-to-image (primary); FAL openai/gpt-image-2/edit; Replicate openai/gpt-image-2 (input_images triggers edit)
}

export enum FluxImageGenerationModelType {
  FLUX_PRO = "black-forest-labs/flux-pro", // https://replicate.com/black-forest-labs/flux-pro
  FLUX_SCHNELL = "black-forest-labs/flux-schnell", // https://replicate.com/black-forest-labs/flux-schnell
  FLUX_DEV = "black-forest-labs/flux-dev", // https://replicate.com/black-forest-labs/flux-dev
  FLUX_2_PRO = "bfl/flux-2-pro", // FAL: fal-ai/flux-2-pro
  FLUX_2_FLEX = "bfl/flux-2-flex", // FAL: fal-ai/flux-2-flex
  FLUX_PRO_KONTEXT = "bfl/flux-pro-kontext", // FAL: fal-ai/flux-pro/kontext
}

// Recraft image generation models (FAL)
export enum RecraftImageGenerationModelType {
  RECRAFT_V4_PRO = "recraft/v4-pro", // FAL: fal-ai/recraft/v4/pro/text-to-image
}

// Qwen image generation models (FAL)
//
// Verified 2026-08-12 (VM-1053): Qwen Image 3 is FAL-only. Replicate 404s on both plausible
// owners (`qwen/qwen-image-3`, `alibaba/qwen-image-3`) and its authenticated catalog search
// surfaces only v1/v2-era ids. Kie.ai (`qwen3/*`) and WaveSpeed (`alibaba/qwen-image-3.0/*`)
// carry the same model but are non-primary providers, so they are recorded and deferred.
// The `-pro` variants on those providers are a SEPARATE, dearer tier — not this model.
export enum QwenImageGenerationModelType {
  QWEN_IMAGE = "qwen/qwen-image", // FAL: fal-ai/qwen-image — enum-only placeholder, still unwired
  QWEN_IMAGE_3 = "qwen/qwen-image-3", // FAL: alibaba/qwen-image-3/text-to-image — $0.04/1K, $0.075/2K
  QWEN_IMAGE_3_EDIT = "qwen/qwen-image-3-edit", // FAL: alibaba/qwen-image-3/edit — 1-3 refs, 1K only
}

// Ideogram image generation models (Kie.ai)
export enum IdeogramImageGenerationModelType {
  IDEOGRAM_V4 = "ideogram/v4",
  IDEOGRAM_V4_EDIT = "ideogram/v4-edit",
  IDEOGRAM_V3 = "ideogram/v3-text-to-image", // Kie.ai: ideogram/v3-text-to-image — Quality/Balanced/Turbo tiers
  IDEOGRAM_V3_EDIT = "ideogram/v3-edit", // Kie.ai: ideogram/v3-edit — image editing with prompt guidance
  IDEOGRAM_V3_REMIX = "ideogram/v3-remix", // Kie.ai: ideogram/v3-remix — I2I remix with style+strength control, $0.0175-0.05/img
  IDEOGRAM_V3_REFRAME = "ideogram/v3-reframe", // Kie.ai: ideogram-reframe — outpainting/reframing with mask, $0.0175-0.05/img
  IDEOGRAM_CHARACTER = "ideogram/character", // Kie.ai: ideogram/character — T2I with character reference images, $0.06-$0.12/img
  IDEOGRAM_CHARACTER_REMIX = "ideogram/character-remix", // Kie.ai: ideogram/character-remix — I2I with character consistency, $0.06-$0.12/img
  IDEOGRAM_CHARACTER_EDIT = "ideogram/character-edit", // Kie.ai: ideogram/character-edit — masked edit with character ref, $0.06-$0.12/img
}

export enum GoogleImageGenerationModelType {
  IMAGEN_4 = "google/imagen-4", // https://replicate.com/google/imagen-4
  IMAGEN_4_FAST = "google/imagen-4-fast", // https://replicate.com/google/imagen-4-fast
  IMAGEN_4_ULTRA = "google/imagen-4-ultra", // https://replicate.com/google/imagen-4-ultra
  NANO_BANANA = "google/nano-banana", // https://replicate.com/google/nano-banana
  NANO_BANANA_PRO = "google/nano-banana-pro", // https://replicate.com/google/nano-banana-pro
  NANO_BANANA_2_1 = "google/nano-banana-2.1", // Distinct version; FAL billing coverage tracked separately.
  // Google's flash tier, NOT a "Pro 2" — Nano Banana Pro above is the separate $0.15 gemini-3-pro-image.
  // FAL: fal-ai/nano-banana-2 (+ /edit), Kie.ai: nano-banana-2. Legacy id + Google's other published
  // name for the same endpoint both resolve here via IMAGE_MODEL_ID_ALIASES.
  NANO_BANANA_2 = "google/nano-banana-2",
  GEMINI_2_5_FLASH_IMAGE = "google/gemini-2.5-flash-image", // https://replicate.com/google/gemini-2.5-flash-image
}

export enum NvidiaImageGenerationModelType {
  SANA = "nvidia/sana:c6b5d2b7459910fec94432e9e1203c3cdce92d6db20f714f1355747990b52fa6",
}

// Kling video generation models - provider-agnostic IDs
// Provider endpoints are mapped in packages/apiutils/src/video/config.ts
export enum KlingAIVideoGenerationModelType {
  // Kling 1.0 (FAL.ai only)
  KLING_1_0_STANDARD = "kling-1.0-standard",
  KLING_1_0_PRO = "kling-1.0-pro",
  // Kling 1.6 (Replicate primary)
  KLING_STANDARD_1_6 = "kling-1.6-standard",
  KLING_PRO_1_6 = "kling-1.6-pro",
  // Kling 2.x (Multi-provider)
  KLING_2_1 = "kling-2.1",
  KLING_2_1_MASTER = "kling-2.1-master", // https://replicate.com/kwaivgi/kling-v2.1-master
  KLING_2_5_TURBO_PRO = "kling-2.5-turbo-pro",
  KLING_2_6 = "kling-2.6",
  // Kling 2.6 Motion Control (Kie.ai) - video-to-video motion transfer at $0.09/sec (1080p)
  KLING_2_6_MOTION_CONTROL = "kling-2.6-motion-control",
  // Kling 3.0 (Multi-provider: Replicate, FAL, Kie.ai)
  KLING_3_0 = "kling-3.0",
  KLING_4_0 = "kling-4.0",
  // Kling 3.0 Omni (Replicate only) - multimodal, native audio, up to 15s, T2V + I2V
  KLING_3_0_OMNI = "kling-3.0-omni",
  // Kling 3.0 Motion Control (Kie.ai) - transfer motion from reference video
  KLING_3_0_MOTION_CONTROL = "kling-3.0-motion-control",
  // Kling O3 / Video 3.0 Omni (FAL) - reference-heavy, native audio, multi-shot
  KLING_O3 = "kling-o3",
  // Kling O3 4K (FAL) - 4K output variant of O3 — fal-ai/kling-video/o3/4k/{t2v,i2v,reference}
  KLING_O3_4K = "kling-o3-4k",
  // Kling 3.0 4K (FAL) - 4K output variant of 3.0 — fal-ai/kling-video/v3/4k/{t2v,i2v}
  KLING_3_0_4K = "kling-3.0-4k",
}

export enum MinimaxVideoGenerationModelType {
  VIDEO_01 = "minimax/video-01",
  HAILUO_02_FAST = "minimax/hailuo-02-fast",
  HAILUO_2_3 = "minimax/hailuo-2.3", // https://replicate.com/minimax/hailuo-2.3
  HAILUO_2_3_FAST = "minimax/hailuo-2.3-fast", // https://replicate.com/minimax/hailuo-2.3-fast
  HAILUO_02 = "minimax/hailuo-02", // https://replicate.com/minimax/hailuo-02 - standard quality (non-fast)
  VIDEO_01_DIRECTOR = "minimax/video-01-director", // https://replicate.com/minimax/video-01-director - T2V+I2V with camera movement commands
  VIDEO_01_LIVE = "minimax/video-01-live", // https://replicate.com/minimax/video-01-live - I2V optimized for animation/Live2D
  HAILUO_02_PRO = "kie-ai/hailuo-02-pro", // https://docs.kie.ai/market/hailuo/02-text-to-video-pro.md — Kie.ai only, 1080p, $0.285/video (41% cheaper than FAL)
  HAILUO_2_3_I2V_PRO = "kie-ai/hailuo-2.3-i2v-pro", // https://docs.kie.ai/market/hailuo/2-3-image-to-video-pro.md — Kie.ai only, 1080p I2V, $0.225-0.45/video
  HAILUO_2_3_I2V_STD = "kie-ai/hailuo-2.3-i2v-std", // https://docs.kie.ai/market/hailuo/2-3-image-to-video-standard.md — Kie.ai only, 768p/1080p I2V, $0.15-0.25/video
  // MiniMax H3 — frontier 2K model. FAL-only (verified 2026-07-31: replicate 404s on
  // minimax/h3 while minimax/hailuo-02 200s on the same key; absent from kie-ai + wavespeed).
  // One model, three FAL modes: t2v, i2v (first/last frame), r2v (multimodal references).
  H3 = "minimax/h3",
}

export enum EHeyGenVideoGenerationModelType {
  HEYGEN_VIDEO = "heygen/heygen-video",
}

export enum GoogleVideoGenerationModelType {
  GEMINI_OMNI_FLASH_PREVIEW = "google/gemini-omni-flash-preview",
  GEMINI_OMNI_FLASH_1_1 = "google/gemini-omni-1.1-flash",
  VEO_2 = "google/veo-2",
  VEO_3 = "google/veo-3",
  VEO_3_FAST = "google/veo-3-fast", // https://replicate.com/google/veo-3-fast
  VEO_3_1 = "google/veo-3.1",
  VEO_3_1_FAST = "google/veo-3.1-fast", // https://replicate.com/google/veo-3.1-fast
  VEO_3_1_LITE = "google/veo-3.1-lite", // https://replicate.com/google/veo-3.1-lite - cost-efficient, native audio, high-volume
}

export enum SeedanceVideoGenerationModelType {
  SEEDANCE_1_LITE = "bytedance/seedance-1-lite", // https://replicate.com/bytedance/seedance-1-lite
  SEEDANCE_1_PRO = "bytedance/seedance-1-pro",
  SEEDANCE_1_PRO_FAST = "bytedance/seedance-1-pro-fast", // https://replicate.com/bytedance/seedance-1-pro-fast
  SEEDANCE_1_5_PRO = "bytedance/seedance-1.5-pro", // https://replicate.com/bytedance/seedance-1.5-pro
  SEEDANCE_2 = "bytedance/seedance-2", // multi-provider: kie-ai (primary), replicate, wavespeed, fal
  SEEDANCE_2_FAST = "bytedance/seedance-2-fast", // multi-provider: kie-ai (primary), replicate, wavespeed, fal — speed-optimized
  SEEDANCE_2_MINI = "bytedance/seedance-2.0-mini", // multi-provider: replicate (primary), wavespeed, fal
  // multi-provider: fal (t2v/i2v/r2v), replicate. No Kie.ai route exists for 2.5.
  // Capability gain over 2.0: 30s max duration (vs 15) and 30/10/10 reference images/videos/audios (vs 9/3/3).
  SEEDANCE_2_5 = "bytedance/seedance-2.5",
}

export enum WavespeedVideoGenerationModelType {
  WAN_2_1_I2V_720P = "wavespeedai/wan-2.1-i2v-720p",
  WAN_2_1_I2V_480P = "wavespeedai/wan-2.1-i2v-480p",
  WAN_2_1_T2V_480P = "wavespeedai/wan-2.1-t2v-480p", // https://replicate.com/wavespeedai/wan-2.1-t2v-480p
  WAN_2_1_T2V_720P_REPLICATE = "wavespeedai/wan-2.1-t2v-720p", // https://replicate.com/wavespeedai/wan-2.1-t2v-720p
  WAN_2_1_T2V_720P = "wavespeed-ai/wan-2.1/t2v-720p", // WaveSpeed Direct API only
  // Wan 2.2 models (cheapest option - supports t2v and i2v)
  WAN_2_2_5B_FAST = "wan-video/wan-2.2-5b-fast", // https://replicate.com/wan-video/wan-2.2-5b-fast
  WAN_2_2_I2V_A14B = "wan-video/wan-2.2-i2v-a14b", // https://replicate.com/wan-video/wan-2.2-i2v-a14b
  // Wan 2.5 models (from wan-video namespace on Replicate)
  WAN_2_5_T2V = "wan-video/wan-2.5-t2v",
  WAN_2_5_I2V = "wan-video/wan-2.5-i2v",
  WAN_2_5_T2V_FAST = "wan-video/wan-2.5-t2v-fast",
  WAN_2_5_I2V_FAST = "wan-video/wan-2.5-i2v-fast",
  // Wan 2.6 models (WaveSpeed Direct API only)
  WAN_2_6_T2V = "alibaba/wan-2.6/text-to-video",
  WAN_2_6_I2V = "alibaba/wan-2.6/image-to-video",
  WAN_2_6_I2V_FLASH = "alibaba/wan-2.6/image-to-video-flash",
  // Wan 2.2 fast variants (Pruna AI optimized, from wan-video namespace)
  WAN_2_2_I2V_FAST = "wan-video/wan-2.2-i2v-fast", // https://replicate.com/wan-video/wan-2.2-i2v-fast
  WAN_2_2_T2V_FAST = "wan-video/wan-2.2-t2v-fast", // https://replicate.com/wan-video/wan-2.2-t2v-fast
  // Wan 2.7 models (Replicate, newest - 1080p, up to 15s, audio sync)
  WAN_2_7_T2V = "wan-video/wan-2.7-t2v", // https://replicate.com/wan-video/wan-2.7-t2v - T2V with audio
  WAN_2_7_I2V = "wan-video/wan-2.7-i2v", // https://replicate.com/wan-video/wan-2.7-i2v - I2V with first/last frame + audio
  // Wan 2.7 R2V (Kie.ai only — reference-to-video with image/video/audio reference inputs)
  WAN_2_7_R2V = "kie-ai/wan-2.7-r2v", // https://docs.kie.ai/market/wan/2-7-r2v.md — $0.12/sec (1080p)
  // Wan 2.7 VideoEdit (Kie.ai only — video-to-video editing with prompt guidance)
  WAN_2_7_VIDEOEDIT = "wan-video/wan-2.7-videoedit", // https://docs.kie.ai/market/wan/2-7-videoedit.md — $0.12/sec (1080p)
  // Wan 2.6 Video-to-Video (Kie.ai only — video editing, $0.35-1.575/video, 30% cheaper than FAL)
  WAN_2_6_V2V = "wan-video/wan-2.6-v2v", // https://docs.kie.ai/market/wan/2-6-video-to-video.md
  // Wan 3.0 standard and Prime (FAL canonical IDs; routed through Replicate and FAL)
  WAN_3_0_T2V = "alibaba/wan-3.0/text-to-video",
  WAN_3_0_I2V = "alibaba/wan-3.0/image-to-video",
  WAN_3_0_PRIME_T2V = "alibaba/wan-3.0-prime/text-to-video",
  WAN_3_0_PRIME_I2V = "alibaba/wan-3.0-prime/image-to-video",
}

export enum OpenAIVideoGenerationModelType {
  SORA_2 = "openai/sora-2", // https://replicate.com/openai/sora-2
  SORA_2_PRO = "openai/sora-2-pro", // https://replicate.com/openai/sora-2-pro
  SORA_2_PRO_STORYBOARD = "openai/sora-2-pro-storyboard", // https://docs.kie.ai/market/sora-2-pro-storyboard.md — $0.75-1.35/video
}

// Pixverse video generation models
export enum PixverseVideoGenerationModelType {
  PIXVERSE_V4_5 = "pixverse/pixverse-v4.5", // https://replicate.com/pixverse/pixverse-v4.5
  PIXVERSE_V5 = "pixverse/pixverse-v5", // https://replicate.com/pixverse/pixverse-v5
  PIXVERSE_V5_6 = "pixverse/pixverse-v5.6", // https://replicate.com/pixverse/pixverse-v5.6
  PIXVERSE_V6 = "pixverse/pixverse-v6", // FAL: fal-ai/pixverse/v6/{t2v,i2v}
  PIXVERSE_C1 = "pixverse/pixverse-c1", // FAL: fal-ai/pixverse/c1/{t2v,i2v} — 1080p, cinematic quality, audio support
  PIXVERSE_C1_TRANSITION = "pixverse/pixverse-c1-transition", // FAL: fal-ai/pixverse/c1/transition — morphing/transition between two images
}

// HunyuanVideo models (via WaveSpeed AI on Replicate)
export enum HunyuanVideoGenerationModelType {
  HUNYUAN_VIDEO_FAST = "wavespeedai/hunyuan-video-fast", // https://replicate.com/wavespeedai/hunyuan-video-fast - Tencent HunyuanVideo, 720p T2V
}

// Runway video generation models
export enum RunwayVideoGenerationModelType {
  GEN4_TURBO = "runwayml/gen4-turbo", // https://replicate.com/runwayml/gen4-turbo - FASTEST high-quality video model (~25s generation)
  GEN4_5 = "runwayml/gen-4.5", // https://replicate.com/runwayml/gen-4.5
  ALEPH = "runway-aleph", // Kie.ai: runway-api/generate-aleph-video — $0.55/video flat rate
}

// Luma video generation models (via FAL)
export enum LumaVideoGenerationModelType {
  RAY_2 = "fal-ai/luma-dream-machine/ray-2", // https://fal.ai/models/fal-ai/luma-dream-machine/ray-2
  RAY_3_2 = "luma/ray-3.2", // WaveSpeed: luma/ray-3.2/{text-to-video,image-to-video}
}

// LTX video generation models (via FAL)
export enum LtxVideoGenerationModelType {
  LTX_2_3_FAST = "fal-ai/ltx-2.3/fast", // https://fal.ai/models/fal-ai/ltx-2.3
  LTX_2_19B = "fal-ai/ltx-2-19b", // https://fal.ai/models/fal-ai/ltx-2-19b — 19B model, high-quality i2v
  LTX_2_3_22B_DISTILLED = "fal-ai/ltx-2.3-22b/distilled", // https://fal.ai/models/fal-ai/ltx-2.3-22b — reference video-to-video
  LTX_VIDEO_13B_DISTILLED = "fal-ai/ltx-video-13b-distilled", // https://fal.ai/models/fal-ai/ltx-video-13b-distilled — 13B distilled, fast i2v
  LTX_2_3_22B_REFERENCE = "fal-ai/ltx-2.3-22b/reference", // https://fal.ai/models/fal-ai/ltx-2.3-22b/reference-video-to-video — base (non-distilled) reference v2v
}

export enum ECreatifyVideoGenerationModelType {
  BOREAL = "creatify/boreal",
}

// Vidu video generation models (via FAL)
export enum ViduVideoGenerationModelType {
  VIDU_Q3 = "fal-ai/vidu/q3", // https://fal.ai/models/fal-ai/vidu/q3 — reference-based video from character images
}

// SkyReels video generation models (via WaveSpeed)
export enum SkyReelsVideoGenerationModelType {
  SKYREELS_V4_T2V = "skywork-ai/skyreels-v4/text-to-video",
  SKYREELS_V4_I2V = "skywork-ai/skyreels-v4/image-to-video",
  SKYREELS_V4_R2V = "skywork-ai/skyreels-v4/reference-to-video",
}

// Grok video generation models (via Kie.ai — grok-imagine endpoints)
/**
 * Black Forest Labs video models. BFL's first video model — the FLUX brand previously existed
 * only as an IMAGE family (`FluxImageGenerationModelType`), so this is a separate video enum.
 * Id follows the existing `bfl/flux-2-pro` convention; the Replicate slug
 * (`black-forest-labs/flux-3`) belongs in `video/config.ts`, not here.
 */
export enum BflVideoGenerationModelType {
  FLUX_3_VIDEO = "bfl/flux-3", // fal: blackforestlabs/flux-3/{text-to-video,image-to-video} · replicate: black-forest-labs/flux-3
}

export enum GrokVideoGenerationModelType {
  GROK_VIDEO = "grok-video", // Kie.ai: grok-imagine/{text-to-video,image-to-video} — $0.015/sec
  GROK_VIDEO_1_5 = "grok-imagine-video-1.5", // Kie.ai: grok-imagine-video-1.5 — I2V preview, $0.0725-$0.125/sec
}

// HappyHorse video generation models (Alibaba, via Kie.ai — happyhorse endpoints)
export enum HappyHorseVideoGenerationModelType {
  HAPPYHORSE_T2V = "happyhorse/text-to-video", // Kie.ai: happyhorse/text-to-video — $0.265/sec (1080p)
  HAPPYHORSE_I2V = "happyhorse/image-to-video", // Kie.ai: happyhorse/image-to-video — $0.265/sec (1080p)
  HAPPYHORSE_R2V = "happyhorse/reference-to-video", // Kie.ai: happyhorse/reference-to-video — $0.265/sec (1080p), character/style reference
  HAPPYHORSE_VIDEO_EDIT = "happyhorse/video-edit", // Kie.ai: happyhorse/video-edit — $0.265/sec (1080p), video-to-video editing
  HAPPYHORSE_1_1 = "alibaba/happyhorse-1.1", // https://replicate.com/alibaba/happyhorse-1.1 — T2V/I2V/R2V (also fal t2v/i2v/r2v, Kie t2v/i2v/r2v, WaveSpeed r2v)
}

// ==================== LIPSYNC MODELS ====================
// Lipsync = image + audio → talking head video. Distinct from text→video models
// (different input shape, different dispatch path). Top-level peer of
// VideoGenerationModelType, NOT a member of it.
//
// Dispatch lives in `apps/vidmax-api/src/inngest/handlers/handleLipsyncVideo.ts`
// and uses `lipsyncModels[modelId].provider` to route to the right submit fn.
export enum ELipsyncModel {
  // WaveSpeed (via Direct API; needs public CDN URLs, slow ~5-10min)
  INFINITETALK_480P = "wavespeed-ai/infinitetalk-480p", // $0.03/sec
  INFINITETALK_720P = "wavespeed-ai/infinitetalk-720p", // $0.06/sec
  // fal.ai (accepts CDN URLs directly, fast ~30-90s)
  AURORA = "fal-ai/creatify/aurora", // $0.07/s (480p), $0.14/s (720p) — best overall lip sync
  OMNIHUMAN_V1_5 = "fal-ai/bytedance/omnihuman/v1.5", // ~$0.04/s — best emotional expression
  VEED_FABRIC = "veed/fabric-1.0", // ~$0.03/s (480p), ~$0.08/s (720p) — cheapest, any image type
}

/** True provider/lab family for a lipsync model (not the run-host). */
export enum ELipsyncModelFamily {
  INFINITETALK = "infinitetalk", // WaveSpeed AI
  AURORA = "aurora", // Creatify
  OMNIHUMAN = "omnihuman", // ByteDance
  FABRIC = "fabric", // Veed
}

/** Provider host that runs a given lipsync model. */
export type ELipsyncProvider = "wavespeed" | "fal"

export interface LipsyncModelConfig {
  family: ELipsyncModelFamily
  provider: ELipsyncProvider
  /** Endpoint string passed to the provider SDK (provider-specific shape). */
  endpoint: string
}

/** Single source of truth: family + provider + endpoint per lipsync model. */
export const lipsyncModels: Record<ELipsyncModel, LipsyncModelConfig> = {
  [ELipsyncModel.INFINITETALK_480P]: {
    family: ELipsyncModelFamily.INFINITETALK,
    provider: "wavespeed",
    endpoint: "wavespeed-ai/infinitetalk",
  },
  [ELipsyncModel.INFINITETALK_720P]: {
    family: ELipsyncModelFamily.INFINITETALK,
    provider: "wavespeed",
    endpoint: "wavespeed-ai/infinitetalk",
  },
  [ELipsyncModel.AURORA]: {
    family: ELipsyncModelFamily.AURORA,
    provider: "fal",
    endpoint: "fal-ai/creatify/aurora",
  },
  [ELipsyncModel.OMNIHUMAN_V1_5]: {
    family: ELipsyncModelFamily.OMNIHUMAN,
    provider: "fal",
    endpoint: "fal-ai/bytedance/omnihuman/v1.5",
  },
  [ELipsyncModel.VEED_FABRIC]: {
    family: ELipsyncModelFamily.FABRIC,
    provider: "fal",
    endpoint: "veed/fabric-1.0",
  },
}

export const zLipsyncModel = z.nativeEnum(ELipsyncModel)
export const zLipsyncModelFamily = z.nativeEnum(ELipsyncModelFamily)
// Alias kept for symmetry with zVideoGenerationModelTypes / zImageGenerationModelTypes.
export const zLipsyncModelTypes = zLipsyncModel
export type LipsyncModelType = ELipsyncModel

export const isLipsyncModel = (modelId: string | undefined): modelId is ELipsyncModel =>
  !!modelId && Object.prototype.hasOwnProperty.call(lipsyncModels, modelId)

export const getLipsyncProvider = (modelId: ELipsyncModel): ELipsyncProvider => lipsyncModels[modelId].provider

export enum MinimaxImageGenerationModelType {
  MINIMAX_IMAGE_01 = "minimax/image-01",
}

export enum EByteDanceImageGenerationModelType {
  SEEDREAM_4 = "bytedance/seedream-4",
  SEEDREAM_4_5 = "bytedance/seedream-4.5", // https://replicate.com/bytedance/seedream-4.5
  SEEDREAM_4_5_EDIT = "bytedance/seedream-4.5-edit", // Kie.ai primary; FAL + Replicate unified endpoint fallbacks
  SEEDREAM_5_LITE = "bytedance/seedream-5-lite", // Kie.ai: seedream/5-lite-text-to-image — $0.0275/image (2K basic / 4K high)
  SEEDREAM_5_LITE_EDIT = "bytedance/seedream-5-lite-edit", // Kie.ai: seedream/5-lite-image-to-image — $0.0275/image (I2I mode)
  SEEDREAM_5_PRO = "bytedance/seedream-5-pro", // FAL primary; Kie + Replicate fallbacks
  SEEDREAM_5_PRO_EDIT = "bytedance/seedream-5-pro-edit", // FAL: bytedance/seedream/v5/pro/edit — up to 10 references
}

export enum PrunaAIImageGenerationModelType {
  Z_IMAGE_TURBO = "prunaai/z-image-turbo", // https://replicate.com/prunaai/z-image-turbo
}

export enum WanImageGenerationModelType {
  WAN_2_7_IMAGE = "wan/image-2.7", // Kie.ai: wan-2-7-image — $0.024/image (standard, 20% cheaper than FAL)
  WAN_2_7_IMAGE_PRO = "wan/image-2.7-pro", // Kie.ai: wan/2-7-image-pro — $0.06/image (pro quality)
}

export enum HiDreamImageGenerationModelType {
  HIDREAM_O1 = "hidream/o1", // FAL: fal-ai/hidream-o1-image — $0.01/megapixel, T2I + reference personalization
}

export enum NucleusImageGenerationModelType {
  NUCLEUS_IMAGE = "nucleus/image", // FAL: fal-ai/nucleus-image — $0.01/megapixel, MoE diffusion T2I
}

// Grok image generation models (xAI). 1.x "Aurora" routes via Kie.ai; 2.0 is FAL-exclusive.
//
// Verified 2026-08-12 (VM-1054): Grok Imagine Image 2.0 exists ONLY on FAL. Replicate
// (`xai/grok-imagine-image`, `xai/grok-imagine-image-quality`), Kie.ai (`grok-imagine/*`) and
// WaveSpeed (`x-ai/grok-imagine-image*`) all carry UNVERSIONED 1.x whose input contracts lack
// 2.0's quality/num_images/output_format. Do not alias 2.0 onto the 1.x ids — the schemas differ.
export enum GrokImageGenerationModelType {
  GROK_IMAGINE_T2I = "grok/imagine-t2i", // Kie.ai: grok-imagine/text-to-image — $0.00625/image (xAI Aurora)
  GROK_IMAGINE_I2I = "grok/imagine-i2i", // Kie.ai: grok-imagine/image-to-image — $0.01/image (xAI Aurora I2I)
  GROK_IMAGINE_2_T2I = "grok/imagine-2-t2i", // FAL: xai/grok-imagine-image/v2.0/text-to-image — $0.04-$0.08/image
  GROK_IMAGINE_2_EDIT = "grok/imagine-2-edit", // FAL: xai/grok-imagine-image/v2.0/edit — same + $0.01/input image
}

// Reve image generation models (FAL only) — strong prompt adherence + text rendering
export enum EReveImageGenerationModelType {
  REVE_2_1 = "reve/2.1", // FAL: reve/2.1/text-to-image — $0.25/image
  REVE_2_1_EDIT = "reve/2.1-edit", // FAL: reve/2.1/edit — single-reference edit, $0.25/image
  REVE_2_1_REMIX = "reve/2.1-remix", // FAL: reve/2.1/remix — multi-reference remix (1-8 images), $0.25/image
}

// Microsoft MAI image generation models (FAL only) — token-billed, so FAL quotes averages/ranges
// rather than a flat per-image price. Not on Replicate or Kie.ai (verified 2026-07-31).
// NOTE: the cheaper non-pro sibling (microsoft/mai-image-2.5, ~$0.05/image) is a DIFFERENT model.
export enum EMaiImageGenerationModelType {
  MAI_IMAGE_2_5_PRO = "mai-image-2.5-pro", // FAL: microsoft/mai-image-2.5-pro — T2I, ~$0.17/image average
  MAI_IMAGE_2_5_PRO_EDIT = "mai-image-2.5-pro-edit", // FAL: microsoft/mai-image-2.5-pro/edit — single-reference edit, $0.18-$0.27/image
}

export enum EKreaImageGenerationModelType {
  KREA_3 = "krea-3",
  KREA_3_EDIT = "krea-3-edit",
}

export enum EMossImageGenerationModelType {
  MOSS_IMAGE = "moss/image",
}

export enum ReplicateVideoGenerationModelType {
  SEEDREAM_LITE = "bytedance/seedance-1-lite",
  SEEDREAM_PRO = "bytedance/seedance-1-pro",
}

export const zImageGenerationModelType = z.union([
  z.nativeEnum(OpenAIImageGenerationModelType),
  z.nativeEnum(FluxImageGenerationModelType),
  z.nativeEnum(GoogleImageGenerationModelType),
  z.nativeEnum(NvidiaImageGenerationModelType),
  z.nativeEnum(MinimaxImageGenerationModelType),
  z.nativeEnum(EByteDanceImageGenerationModelType),
  z.nativeEnum(PrunaAIImageGenerationModelType),
  z.nativeEnum(RecraftImageGenerationModelType),
  z.nativeEnum(QwenImageGenerationModelType),
  z.nativeEnum(IdeogramImageGenerationModelType),
  z.nativeEnum(WanImageGenerationModelType),
  z.nativeEnum(HiDreamImageGenerationModelType),
  z.nativeEnum(NucleusImageGenerationModelType),
  z.nativeEnum(GrokImageGenerationModelType),
  z.nativeEnum(EReveImageGenerationModelType),
  z.nativeEnum(EMaiImageGenerationModelType),
  z.nativeEnum(EMossImageGenerationModelType),
  z.nativeEnum(EKreaImageGenerationModelType),
])

// Image Quality
export enum GptImage1Quality {
  Low = "low",
  Medium = "medium",
  High = "high",
}
export type VideoGenerationModelType =
  | KlingAIVideoGenerationModelType
  | MinimaxVideoGenerationModelType
  | EHeyGenVideoGenerationModelType
  | GoogleVideoGenerationModelType
  | SeedanceVideoGenerationModelType
  | WavespeedVideoGenerationModelType
  | OpenAIVideoGenerationModelType
  | PixverseVideoGenerationModelType
  | RunwayVideoGenerationModelType
  | HunyuanVideoGenerationModelType
  | LumaVideoGenerationModelType
  | LtxVideoGenerationModelType
  | ECreatifyVideoGenerationModelType
  | ViduVideoGenerationModelType
  | SkyReelsVideoGenerationModelType
  | GrokVideoGenerationModelType
  | HappyHorseVideoGenerationModelType
  | BflVideoGenerationModelType

export type ImageGenerationModelType =
  | OpenAIImageGenerationModelType
  | FluxImageGenerationModelType
  | GoogleImageGenerationModelType
  | NvidiaImageGenerationModelType
  | MinimaxImageGenerationModelType
  | EByteDanceImageGenerationModelType
  | PrunaAIImageGenerationModelType
  | RecraftImageGenerationModelType
  | QwenImageGenerationModelType
  | IdeogramImageGenerationModelType
  | WanImageGenerationModelType
  | HiDreamImageGenerationModelType
  | NucleusImageGenerationModelType
  | GrokImageGenerationModelType
  | EReveImageGenerationModelType
  | EMaiImageGenerationModelType
  | EMossImageGenerationModelType
  | EKreaImageGenerationModelType

/**
 * Non-canonical image model ids that resolve to a registered model.
 *
 * An alias is how we say "same model, another name" WITHOUT a second registry entry — a duplicate
 * entry would need its own providers, pricing, config, family and picker row, and the two copies
 * would drift the first time a price moved.
 *
 * - `google/nano-banana-pro-2` was this model's original id. It named the wrong tier (this is Nano
 *   Banana 2, Google's flash-tier image model; Nano Banana *Pro* is the separate, dearer
 *   gemini-3-pro-image). Every job row, saved picker choice and preset written before the rename
 *   still carries it, so it keeps resolving instead of stranding that history.
 * - `google/gemini-3.1-flash-image-preview` is Google's/FAL's other published name for the same
 *   endpoint — fal-ai/gemini-3.1-flash-image-preview and fal-ai/nano-banana-2 are one model at one
 *   price. Callers who know it by that name land on the same registered model.
 *
 * Applied at the request boundary (`zImageGenerationModelTypes`, `imageGenerationModelSchema`) and
 * inside the registry/pricing lookups, so both live requests and historical rows resolve.
 */
export const IMAGE_MODEL_ID_ALIASES: Readonly<Record<string, ImageGenerationModelType>> = {
  "google/nano-banana-pro-2": GoogleImageGenerationModelType.NANO_BANANA_2,
  "google/gemini-3.1-flash-image-preview": GoogleImageGenerationModelType.NANO_BANANA_2,
}

/** Resolve an alias to its canonical model id; any other value is returned untouched. */
export function normalizeImageModelId(modelId: ImageGenerationModelType): ImageGenerationModelType
export function normalizeImageModelId(modelId: string): string
export function normalizeImageModelId(modelId: unknown): unknown
export function normalizeImageModelId(modelId: unknown): unknown {
  if (typeof modelId !== "string") return modelId
  return IMAGE_MODEL_ID_ALIASES[modelId] ?? modelId
}

export enum ImageSize {
  Size576x1024 = "576x1024",
  Size1024x1024 = "1024x1024",
  Size1024x1536 = "1024x1536",
  Size1024x1792 = "1024x1792",
  Size1536x1024 = "1536x1024",
  Size1792x1024 = "1792x1024",
  Size1200x675 = "1200x675",
  Size1024x768 = "1024x768",
}

export const ImageSizeMap = {
  [ImageSize.Size576x1024]: { width: 576, height: 1024 },
  [ImageSize.Size1024x1024]: { width: 1024, height: 1024 },
  [ImageSize.Size1024x1792]: { width: 1024, height: 1792 },
  [ImageSize.Size1792x1024]: { width: 1792, height: 1024 },
  [ImageSize.Size1200x675]: { width: 1200, height: 675 },
  [ImageSize.Size1024x768]: { width: 1024, height: 768 },
}

export enum EAspectRatio {
  "1x1" = "1:1",
  "4x3" = "4:3",
  "16x9" = "16:9",
  "9x16" = "9:16",
}

export const ImageSizeToAspectRatioMap = {
  [ImageSize.Size576x1024]: EAspectRatio["9x16"],
  [ImageSize.Size1024x1024]: EAspectRatio["1x1"],
  [ImageSize.Size1024x1792]: EAspectRatio["16x9"],
  [ImageSize.Size1792x1024]: EAspectRatio["16x9"],
  [ImageSize.Size1200x675]: EAspectRatio["16x9"],
  [ImageSize.Size1024x768]: EAspectRatio["4x3"],
}

export const AspectRatioToImageSizeMap: Record<EAspectRatio, ImageSize> = {
  [EAspectRatio["1x1"]]: ImageSize.Size1024x1024,
  [EAspectRatio["9x16"]]: ImageSize.Size576x1024,
  [EAspectRatio["16x9"]]: ImageSize.Size1792x1024,
  [EAspectRatio["4x3"]]: ImageSize.Size1024x768,
}

export enum ImageQuality {
  HD = "hd",
  Standard = "standard",
}

// Zod schemas for image-related enums
// Request-validation union for assets.generateImage. MUST list every image-model enum that
// IMAGE_MODEL_PROVIDERS (registry.ts) registers — a registered-but-unlisted model passes the UI
// dropdown then fails Zod here ("Invalid enum value"). Keep in sync with registry.ts.
// Aliases (legacy + vendor spellings) normalize before the union sees them — see
// IMAGE_MODEL_ID_ALIASES. Without this an old persisted id fails with "Invalid enum value".
export const zImageGenerationModelTypes = z.preprocess(
  normalizeImageModelId,
  z.union([
    z.nativeEnum(OpenAIImageGenerationModelType),
    z.nativeEnum(FluxImageGenerationModelType),
    z.nativeEnum(GoogleImageGenerationModelType),
    z.nativeEnum(NvidiaImageGenerationModelType),
    z.nativeEnum(MinimaxImageGenerationModelType),
    z.nativeEnum(EByteDanceImageGenerationModelType),
    z.nativeEnum(PrunaAIImageGenerationModelType),
    z.nativeEnum(ReplicateVideoGenerationModelType),
    z.nativeEnum(RecraftImageGenerationModelType),
    z.nativeEnum(QwenImageGenerationModelType),
    z.nativeEnum(IdeogramImageGenerationModelType),
    z.nativeEnum(WanImageGenerationModelType),
    z.nativeEnum(HiDreamImageGenerationModelType),
    z.nativeEnum(NucleusImageGenerationModelType),
    z.nativeEnum(GrokImageGenerationModelType),
    z.nativeEnum(EReveImageGenerationModelType),
    z.nativeEnum(EMaiImageGenerationModelType),
    z.nativeEnum(EMossImageGenerationModelType),
    z.nativeEnum(EKreaImageGenerationModelType),
  ]),
)

// Zod schema for video generation models
export const zVideoGenerationModelTypes = z.union([
  z.nativeEnum(EHeyGenVideoGenerationModelType),
  z.nativeEnum(KlingAIVideoGenerationModelType),
  z.nativeEnum(MinimaxVideoGenerationModelType),
  z.nativeEnum(GoogleVideoGenerationModelType),
  z.nativeEnum(SeedanceVideoGenerationModelType),
  z.nativeEnum(WavespeedVideoGenerationModelType),
  z.nativeEnum(OpenAIVideoGenerationModelType),
  z.nativeEnum(PixverseVideoGenerationModelType),
  z.nativeEnum(RunwayVideoGenerationModelType),
  z.nativeEnum(HunyuanVideoGenerationModelType),
  z.nativeEnum(LumaVideoGenerationModelType),
  z.nativeEnum(LtxVideoGenerationModelType),
  z.nativeEnum(ECreatifyVideoGenerationModelType),
  z.nativeEnum(ViduVideoGenerationModelType),
  z.nativeEnum(SkyReelsVideoGenerationModelType),
  z.nativeEnum(GrokVideoGenerationModelType),
  z.nativeEnum(HappyHorseVideoGenerationModelType),
  z.nativeEnum(BflVideoGenerationModelType),
])

// Zod schema for avatar/lipsync-capable model ids — standard video models (Seedance
// avatar path) plus dedicated lipsync models (InfiniteTalk, OmniHuman, Aurora, Fabric).
// `ELipsyncModel` is a peer of VideoGenerationModelType, not a member, so endpoints
// that accept both (e.g. media.generateAvatarVideo) must use this union.
export const zAvatarVideoGenerationModelTypes = z.union([zVideoGenerationModelTypes, z.nativeEnum(ELipsyncModel)])

// Zod schema for audio generation models (extensible — add new model enums to this union)
export const zAudioGenerationModelTypes = z.union([
  z.nativeEnum(SunoAudioGenerationModelType),
  z.nativeEnum(MiniMaxAudioModelType),
  z.nativeEnum(ResembleAIAudioModelType),
  z.nativeEnum(QwenAudioModelType),
  z.nativeEnum(InworldAudioModelType),
  z.nativeEnum(GoogleAudioModelType),
])
